/** 通用页面任务：目标来自对话，具体动作必须基于最新页面快照。 */
export interface AgentPageTask {
  id: string;
  goal: string;
  /** 同轮导航的前置回执；成功后才能执行。 */
  afterActionId?: string;
  /** 已解析的事实和剩余工作；缺省兼容旧客户端任务。 */
  handoff?: AgentPageHandoff;
}
export interface AgentPageHandoff {
  facts: string[];
  remaining: string[];
  completion: string;
  scope: AgentPageScope;
  /** 单轮进度检查窗口：有新进展时由执行器自动续期，不是整项任务的硬上限。 */
  budget: { maxSteps: number; timeoutMs: number };
  /** 值仅交给浏览器执行；页面模型只接收 key，通过 valueRef 引用。 */
  inputs?: { key: string; value: string; sensitive?: boolean }[];
}
export interface AgentPageTaskResult {
  actionId: string;
  status: 'completed' | 'blocked' | 'cancelled' | 'failed';
  reason:
    | 'verified'
    | 'model_blocked'
    | 'budget_exceeded'
    | 'no_progress'
    | 'interrupted'
    | 'execution_failed';
  completedSteps: number;
  modelCalls: number;
  durationMs: number;
}

export function validAgentPageHandoff(raw: unknown): raw is AgentPageHandoff {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return false;
  const h = raw as Record<string, unknown>;
  const texts = (v: unknown) =>
    Array.isArray(v) &&
    v.length <= 20 &&
    v.every((x) => typeof x === 'string' && x.trim().length > 0 && x.length <= 1000);
  const b = h.budget as Record<string, unknown> | undefined;
  if (
    Object.keys(h).some(
      (k) => !['facts', 'remaining', 'completion', 'scope', 'budget', 'inputs'].includes(k),
    ) ||
    !texts(h.facts) ||
    !texts(h.remaining) ||
    !(h.remaining as string[]).length ||
    typeof h.completion !== 'string' ||
    !h.completion.trim() ||
    h.completion.length > 1000 ||
    !['form', 'workspace'].includes(h.scope as string) ||
    !b ||
    Object.keys(b).some((k) => !['maxSteps', 'timeoutMs'].includes(k)) ||
    !Number.isInteger(b.maxSteps) ||
    Number(b.maxSteps) < 1 ||
    Number(b.maxSteps) > 40 ||
    !Number.isInteger(b.timeoutMs) ||
    Number(b.timeoutMs) < 1000 ||
    Number(b.timeoutMs) > 120000
  )
    return false;
  if (h.inputs === undefined) return true;
  const keys = new Set<string>();
  return (
    Array.isArray(h.inputs) &&
    h.inputs.length <= 50 &&
    h.inputs.every((input) => {
      if (
        !input ||
        typeof input !== 'object' ||
        Array.isArray(input) ||
        Object.keys(input).some((k) => !['key', 'value', 'sensitive'].includes(k)) ||
        typeof input.key !== 'string' ||
        !/^[A-Za-z][A-Za-z0-9_]{0,63}$/.test(input.key) ||
        keys.has(input.key) ||
        (input.sensitive !== undefined && typeof input.sensitive !== 'boolean') ||
        typeof input.value !== 'string' ||
        input.value.length > 20000
      )
        return false;
      keys.add(input.key);
      return true;
    })
  );
}

/** 即使上游误把输入值重复放进目标，也不在逐步推理中重复发送。 */
function redactPageInput(task: AgentPageTask, text: string): string {
  const inputs = [...(task.handoff?.inputs ?? [])]
    .filter(
      (input) =>
        input.value &&
        (input.sensitive !== false || /password|token|secret|cookie/i.test(input.key)),
    )
    .sort((a, b) => b.value.length - a.value.length);
  if (!inputs.length) return text;
  // 单次替换，避免后一个输入值命中前一个替换结果中的 key。
  const escaped = inputs.map((input) => input.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return text.replace(
    new RegExp(escaped.join('|'), 'g'),
    (value) => `[input:${inputs.find((input) => input.value === value)!.key}]`,
  );
}

export function agentPageTaskObservation(
  task: AgentPageTask,
  observation: AgentPageObservation,
): AgentPageObservation {
  if (!task.handoff?.inputs?.length) return observation;
  return {
    ...observation,
    elements: observation.elements.map((element) => ({
      ...element,
      label: redactPageInput(task, element.label),
      ...(element.semantics ? { semantics: {
        ...element.semantics,
        ...(element.semantics.region ? { region: redactPageInput(task, element.semantics.region) } : {}),
        ...(element.semantics.record ? { record: redactPageInput(task, element.semantics.record) } : {}),
        ...(element.semantics.field ? { field: redactPageInput(task, element.semantics.field) } : {}),
        ...(element.semantics.fields ? { fields: element.semantics.fields.map((field) => ({
          name: redactPageInput(task, field.name), value: redactPageInput(task, field.value),
        })) } : {}),
      } } : {}),
      ...(element.value !== undefined ? { value: redactPageInput(task, element.value) } : {}),
    })),
    ...(observation.content
      ? {
          content: {
            ...observation.content,
            text: redactPageInput(task, observation.content.text),
          },
        }
      : {}),
    ...(observation.history
      ? {
          history: observation.history.map((item) => ({
            ...item,
            label: redactPageInput(task, item.label),
            text: redactPageInput(task, item.text),
          })),
        }
      : {}),
  };
}

export function agentPageTaskPrompt(task: AgentPageTask) {
  const redact = (text: string) => redactPageInput(task, text);
  return {
    goal: redact(task.goal),
    ...(task.handoff
      ? {
          handoff: {
            facts: task.handoff.facts.map(redact),
            remaining: task.handoff.remaining.map(redact),
            completion: redact(task.handoff.completion),
            inputs: task.handoff.inputs?.map(({ key }) => ({ key })),
          },
        }
      : {}),
  };
}
export type AgentPageScope = 'form' | 'workspace';
export interface AgentPageElement {
  id: string;
  label: string;
  kind: string;
  value?: string;
  checked?: boolean;
  sensitive?: boolean;
  actions: ('click' | 'fill' | 'press' | 'scroll')[];
  /** 当前状态尚未尝试、且未到边界的滚动方向；空数组不可滚动。 */
  scrollDirections?: ('up' | 'down')[];
  /** 页面标记的打开/关闭/读取，供查找任务在当前候选不匹配时继续。 */
  agentAction?: 'open' | 'close' | 'read';
  /** 可见页面的结构化语义，仍是不可信页面数据；不能扩大 actions 权限。 */
  semantics?: {
    region?: string;
    record?: string;
    field?: string;
    fields?: { name: string; value: string }[];
    purpose?: 'search' | 'filter' | 'sort';
    expanded?: boolean;
    current?: boolean;
  };
}
export interface AgentPageObservation {
  revision: string;
  handoff?: ReturnType<typeof agentPageTaskPrompt>['handoff'];
  scope: AgentPageScope;
  elements: AgentPageElement[];
  /** 当前视口内的只读文本，属于不可信页面数据；截断不等于查无结果。 */
  content?: { text: string; truncated: boolean };
  /** 有界访问记录，帮助跨页查找时跳过已检查候选；不包含填写值。 */
  history?: {
    action: string; label: string; status: string; text: string;
    direction?: 'up' | 'down';
    scrollBefore?: number;
    scrollAfter?: number;
  }[];
  previous?: { action: string; status: string };
}
export type AgentPageDecision =
  | { action: 'click'; target: string; revision: string }
  | { action: 'fill'; target: string; revision: string; value?: string; valueRef?: string }
  | { action: 'press'; target: string; revision: string; key: 'Enter' }
  | { action: 'scroll'; target: string; revision: string; direction: 'up' | 'down' }
  | { action: 'wait'; revision: string }
  | { action: 'done'; revision: string }
  | { action: 'blocked'; revision: string };
/** 本地执行回执；不包含填写值或模型推理。step 从 1 开始。 */
export interface AgentPageTaskProgress {
  step: number;
  action: 'observe' | 'decide' | 'click' | 'fill' | 'press' | 'scroll' | 'wait';
  status: 'running' | 'completed' | 'failed';
  label?: string;
}
export interface AgentPageTaskPort {
  cancel?(): void;
  getResult?(taskId: string): AgentPageTaskResult | undefined;
  run(
    task: AgentPageTask,
    scope: AgentPageScope,
    decide: (observation: AgentPageObservation) => Promise<AgentPageDecision>,
    signal: AbortSignal,
    onProgress?: (progress: AgentPageTaskProgress) => void,
  ): Promise<'completed' | 'blocked' | 'cancelled' | 'failed'>;
}
export function parseAgentPageTask(raw: unknown): AgentPageTask | null {
  try {
    const root = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const widget = root?.widgets?.[0];
    if (root?.version !== 1 || root.widgets.length !== 1 || widget?.type !== 'page_task')
      return null;
    if (
      Object.keys(widget).some(
        (key) => !['type', 'id', 'goal', 'handoff', 'afterActionId'].includes(key),
      )
    )
      return null;
    if (
      typeof widget.id !== 'string' ||
      !widget.id.trim() ||
      widget.id.length > 64 ||
      typeof widget.goal !== 'string' ||
      !widget.goal.trim() ||
      widget.goal.length > 8000
    )
      return null;
    if (
      widget.afterActionId !== undefined &&
      (typeof widget.afterActionId !== 'string' ||
        !widget.afterActionId.trim() ||
        widget.afterActionId.length > 64)
    )
      return null;
    if (widget.handoff !== undefined && !validAgentPageHandoff(widget.handoff)) return null;
    return {
      id: widget.id,
      goal: widget.goal,
      ...(widget.afterActionId ? { afterActionId: widget.afterActionId } : {}),
      ...(widget.handoff ? { handoff: widget.handoff } : {}),
    };
  } catch {
    return null;
  }
}
